import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

/**
 * At most `max` requests of this client in flight; the rest wait their turn.
 *
 * For the WRITE service, whose Lambda is capped at two concurrent executions
 * (`reserved_concurrent_executions = 2`, infra/terraform/live/internal/lambda.tf
 * — each holds one write connection to Aurora). An organization's page asks it
 * for nine things at once: the catalogues of every form on the page and the
 * roster. AWS throttled the rest before they reached the function — no log line,
 * only `Throttles` — and the roster, which does not retry quickly, showed «No se
 * pudo consultar Keycloak» until its next minute (2026-10-12). Waiting here costs
 * a couple of hundred milliseconds per request; being throttled cost a minute.
 *
 * Registered BEFORE the auth interceptors on purpose: request interceptors run
 * last-registered first, so the token is refreshed before a slot is taken; and
 * response interceptors run first-registered first, so a slot is released
 * before the auth interceptor replays a 401 — the replay takes a slot of its own.
 */
export function limitConcurrency(client: AxiosInstance, max: number): void {
  let inFlight = 0
  const waiting: Array<() => void> = []

  const release = (config: unknown) => {
    const c = config as (InternalAxiosRequestConfig & { _slot?: boolean }) | undefined
    if (!c?._slot) return
    c._slot = false
    inFlight -= 1
    const next = waiting.shift()
    if (next) next()
  }

  client.interceptors.request.use(async (config) => {
    if (inFlight >= max) {
      await new Promise<void>((resolve) => waiting.push(resolve))
    }
    inFlight += 1
    ;(config as InternalAxiosRequestConfig & { _slot?: boolean })._slot = true
    return config
  })

  client.interceptors.response.use(
    (response) => {
      release(response.config)
      return response
    },
    (error) => {
      release(error?.config)
      return Promise.reject(error)
    },
  )
}
